import { Link, useNavigate, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import EmptyState from "@/components/base/EmptyState";
import { newsList } from "@/mocks/news";
import { usePageTitle } from "@/hooks/usePageMeta";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const item = newsList.find((n) => n.id === id);

  usePageTitle(
    item ? `${item.title} - CC视频` : "资讯内容不存在 - CC视频",
    item
      ? item.summary
      : "该资讯内容不存在或已被删除，请返回资讯列表浏览其他内容。",
  );

  if (!item) {
    return (
      <SiteLayout>
        <EmptyState
          icon="ri-article-line"
          title="资讯内容不存在"
          description="这篇资讯可能已被移除，或链接地址有误。你可以返回资讯列表继续阅读。"
        >
          <Link
            to="/news"
            className="mt-6 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors inline-flex items-center whitespace-nowrap cursor-pointer"
          >
            返回资讯列表
          </Link>
        </EmptyState>
      </SiteLayout>
    );
  }

  const others = newsList.filter((n) => n.id !== item.id).slice(0, 4);

  return (
    <SiteLayout>
      <article className="w-full">
        <header className="w-full bg-primary-950">
          <div className="w-full px-4 md:px-6 py-8 md:py-12">
            <nav className="flex items-center gap-1.5 text-xs text-background-400 mb-5 flex-wrap">
              <Link to="/" className="hover:text-accent-400 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
              <Link to="/news" className="hover:text-accent-400 transition-colors">
                影视资讯
              </Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
              <span className="text-accent-400">{item.category}</span>
            </nav>

            <div className="max-w-3xl">
              <div className="flex flex-wrap items-center gap-3 text-xs text-background-400">
                <span className="px-2 py-0.5 rounded bg-accent-500 text-primary-950 font-medium">
                  {item.category}
                </span>
                <span className="font-label">{item.publishedAt}</span>
                <span>来源：{item.source}</span>
              </div>
              <h1 className="mt-4 font-heading text-2xl md:text-4xl font-black text-background-50 leading-tight tracking-tight">
                {item.title}
              </h1>
              <p className="mt-4 text-sm md:text-base text-background-300 leading-relaxed">
                {item.summary}
              </p>
            </div>
          </div>
        </header>

        <div className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10">
            <div className="lg:col-span-8 min-w-0">
              <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200 mb-8">
                <img
                  src={item.cover}
                  alt={item.title}
                  title={`${item.title} - CC视频资讯`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="max-w-[720px]">
                {item.content.map((block, i) => {
                  if (block.type === "h") {
                    return (
                      <h2
                        key={i}
                        className="font-heading text-lg md:text-xl font-bold text-foreground-950 mt-8 mb-3 first:mt-0"
                      >
                        {block.text}
                      </h2>
                    );
                  }
                  if (block.type === "quote") {
                    return (
                      <blockquote
                        key={i}
                        className="my-6 pl-4 md:pl-5 border-l-2 border-accent-500 text-base md:text-lg text-foreground-800 leading-relaxed"
                      >
                        {block.text}
                      </blockquote>
                    );
                  }
                  if (block.type === "img") {
                    return (
                      <figure key={i} className="my-7">
                        <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
                          <img
                            src={block.src}
                            alt={block.caption}
                            title={`${item.title} - 配图 - CC视频`}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <figcaption className="mt-2.5 text-xs text-foreground-500 leading-relaxed">
                          {block.caption}
                        </figcaption>
                      </figure>
                    );
                  }
                  return (
                    <p
                      key={i}
                      className="text-sm md:text-[15px] text-foreground-700 leading-[1.9] md:leading-[2] mb-5"
                    >
                      {block.text}
                    </p>
                  );
                })}
              </div>

              <div className="mt-10 pt-6 border-t border-background-200 flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-secondary-400 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
                  返回资讯列表
                </button>
                <p className="text-xs text-foreground-400">
                  本文为演示内容，著作权归原权利人所有
                </p>
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="rounded-lg border border-background-200 bg-background-100 p-4 md:p-5 sticky top-24">
                <h2 className="font-heading text-sm font-bold text-foreground-950 mb-4">
                  相关文章推荐
                </h2>
                <ul className="space-y-3.5">
                  {others.map((n) => (
                    <li key={n.id}>
                      <Link
                        to={`/news/detail/${n.id}`}
                        className="group flex gap-3 cursor-pointer"
                      >
                        <div className="w-16 h-11 rounded overflow-hidden bg-background-200 shrink-0">
                          <img
                            src={n.cover}
                            alt={n.title}
                            title={`${n.title} - CC视频资讯`}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs text-foreground-900 leading-snug line-clamp-2-custom group-hover:text-accent-600 transition-colors">
                            {n.title}
                          </p>
                          <p className="mt-1 text-[10px] text-foreground-400 font-label">
                            {n.publishedAt}
                          </p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/news"
                  className="mt-5 flex items-center justify-center h-9 rounded-md bg-background-50 border border-background-200 text-xs text-foreground-700 hover:border-secondary-400 transition-colors whitespace-nowrap cursor-pointer"
                >
                  查看全部资讯
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </article>
    </SiteLayout>
  );
}